import { TYPE_CUSTOM_FIELD } from "constants/common";
import THEME from "constants/themes";
import DynamicWidgetContext from "contexts/DynamicWidget";
import { WidgetFieldTypes, WidgetSectionTypes } from "models/DynamicWidget";
import { GoogleSelectedMediaTypes } from "models/Widget";
import { ReactNode, useContext } from "react";
import { StyledRow, StyledWrapper } from "styled-components/Common/CommonStyled";
import {
	StyledHelperText,
	StyledSectionTitle,
	StyledSectionTitleWrapper,
	StyledWidgetSectionWrapper,
} from "styled-components/DynamicWidget/DynamicWidgetStyled";
import DynamicHeroIcon from "../DynamicHeroIcon";
import SelectedFileList from "../SelectedFileList";
import UploadSection from "../UploadSection";
import WidgetField from "./WidgetField";

type WidgetSectionProps = {
	section: WidgetSectionTypes;
};

const WidgetSection = (props: WidgetSectionProps) => {
	const { section } = props;
	const {
		title = "",
		helperText = "",
		icon = "",
		fields = [],
		isRemovedHeader = false,
	} = section || {};
	const {
		localFileList,
		setLocalFileList,
		setFieldsValue,
		formValues,
		setFormValues,
		googleRequestData,
		setGoogleRequestData,
		selectedSocialVideoList,
		setSelectedSocialVideoList,
	} = useContext(DynamicWidgetContext);

	const renderSectionTitle = () => {
		return (
			<StyledSectionTitleWrapper
				margin={isRemovedHeader || (!icon && !title && !helperText) ? "0" : "0 0 24px 0"}>
				{icon && (
					<StyledWrapper
						background={THEME.colors.gray5}
						borderRadius="50%"
						width="40px"
						height="40px"
						display="flex"
						alignItems="center"
						justifyContent="center">
						<DynamicHeroIcon name={icon} />
					</StyledWrapper>
				)}
				{title && <StyledSectionTitle>{title}</StyledSectionTitle>}
				{helperText && <StyledHelperText>{helperText}</StyledHelperText>}
			</StyledSectionTitleWrapper>
		);
	};

	const renderUploadFileList = (uploadFieldDetails: WidgetFieldTypes) => {
		let content: ReactNode | null = null;
		const sources = [
			{
				list: localFileList,
				setList: setLocalFileList,
			},
			{
				list: selectedSocialVideoList,
				setList: setSelectedSocialVideoList,
			},
			{
				list: googleRequestData.selectedMediaList,
				setList: (updatedList: typeof googleRequestData.selectedMediaList) =>
					setGoogleRequestData({ ...googleRequestData, selectedMediaList: updatedList }),
				transform: (list: typeof googleRequestData.selectedMediaList) =>
					list.map((item) => ({
						...item,
						name: item.fileName,
						error: { ...item.limitationError },
					})),
			},
		];

		sources.forEach(({ list, setList, transform }) => {
			if (list?.length) {
				const displayList = transform ? transform(list) : list;
				content = (
					<SelectedFileList
						fileList={displayList}
						onRemoveFile={(index: number) => {
							const updatedList = list.filter((_, i) => i !== index);
							setList(updatedList as GoogleSelectedMediaTypes[]);
						}}
					/>
				);
			}
		});

		return content || <UploadSection uploadFieldDetails={uploadFieldDetails} />;
	};

	const renderFields = () => {
		const existDropzone = fields.some((item) => item.type === TYPE_CUSTOM_FIELD.dropzone);
		const fieldProps = {
			setFieldsValue,
			formValues,
			setFormValues,
		};
		if (existDropzone) {
			const filteredList = fields.filter((item) => item.type !== TYPE_CUSTOM_FIELD.dropzone);
			const uploadFieldDetails = fields.find(
				(item) => item.type === TYPE_CUSTOM_FIELD.dropzone,
			) as WidgetFieldTypes;

			return (
				<>
					{renderUploadFileList(uploadFieldDetails)}

					{!!filteredList.length &&
						(!!localFileList.length ||
							!!googleRequestData?.selectedMediaList.length ||
							!!selectedSocialVideoList?.length) && (
							<StyledWidgetSectionWrapper
								background="rgba(224, 227, 229, 0.20)"
								border="none"
								margin="16px 0 0 0">
								<StyledRow gutter={[12, 12]}>
									{filteredList.map((field, index) => {
										return <WidgetField key={index} field={field} {...fieldProps} />;
									})}
								</StyledRow>
							</StyledWidgetSectionWrapper>
						)}

					{!localFileList.length ? (
						<></>
					) : (
						<UploadSection uploadFieldDetails={uploadFieldDetails} />
					)}
				</>
			);
		}

		return (
			<StyledRow gutter={[12, 12]}>
				{fields.map((field, index) => {
					return <WidgetField key={index} field={field} {...fieldProps} />;
				})}
			</StyledRow>
		);
	};

	return (
		<StyledWidgetSectionWrapper>
			{renderSectionTitle()}
			{renderFields()}
		</StyledWidgetSectionWrapper>
	);
};

export default WidgetSection;
